/**
 * Scope facts — where declarations live (IA spec §5).
 *
 * The core requirement: THREE states, visually and textually distinct —
 *  1. attested covered      — a current attestation asserts coverage;
 *  2. attested known gap    — a current attestation DISCLOSES a segment as not
 *                             monitored. A strength, not a failure: the entity
 *                             documented its own boundary. Never rendered like (3);
 *  3. not attested          — nothing asserted. Unknown, not disclosed.
 *
 * Facts are append-only: this page records new facts only — it offers no
 * edit or supersede action (S14 G1b T7). Lineage from earlier supersessions
 * stays visible: a superseded fact stays listed with the fact that superseded
 * it. Basis is required — an attestation
 * without a stated basis is a signature on nothing (the backend refuses too;
 * this form simply does not offer the path).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import { fmtTs, fmtDate } from '../utils/complianceFormat';

const KIND_LABELS = {
  esp_boundary: 'ESP boundaries',
  eap_list: 'Declared EAPs',
  expected_ports_baseline: 'Expected-port baselines',
  vendor_identity_mapping: 'Vendor identity mappings',
  evaluation_completion_event: 'Evaluation completion events',
  sensor_coverage_attestation: 'Sensor-coverage attestations',
};

const STATUS_STYLES = {
  current: 'bg-green-900/40 text-green-300 border-green-700',
  expired: 'bg-red-900/40 text-red-300 border-red-700',
  superseded: 'bg-gray-700/40 text-gray-400 border-gray-600',
};

function FactCard({ f }) {
  return (
    <div data-testid={`fact-${f.fact_id}`}
         className={`bg-gray-800 rounded-xl border p-4 ${f.status === 'superseded' ? 'border-gray-700 opacity-70' : 'border-gray-600'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white font-mono">{f.fact_id}</div>
          <div className="text-xs text-gray-400 mt-1">
            Asserted by <span className="text-gray-200">{f.asserted_by}</span> at {fmtTs(f.asserted_at)}
          </div>
          <div className="text-xs text-gray-400 mt-1">
            Valid {fmtDate(f.valid_from)} → {f.valid_until ? fmtDate(f.valid_until) : `${fmtDate(f.effective_until)} (default cadence)`}
          </div>
          <div className="text-xs text-gray-300 mt-2">{f.basis}</div>
          {f.segments_covered?.length > 0 && (
            <div data-testid={`covered-${f.fact_id}`} className="text-xs text-green-300 mt-2">
              Attested covered: {f.segments_covered.join(', ')}
            </div>
          )}
          {f.known_gaps?.length > 0 && (
            <div data-testid={`gap-${f.fact_id}`} className="text-xs text-blue-300 mt-1">
              Disclosed known gap (documented, not monitored): {f.known_gaps.join(', ')}
            </div>
          )}
          {f.superseded_by && (
            <div className="text-xs text-gray-500 mt-2">Superseded by {f.superseded_by} — retained as lineage.</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`inline-block px-2 py-0.5 text-[11px] rounded-full border ${STATUS_STYLES[f.status] || STATUS_STYLES.superseded}`}>
            {f.status}
          </span>
        </div>
      </div>
    </div>
  );
}

function FactForm({ initialKind, sites, onClose, onDone }) {
  // Sanitize: an arriving ?kind= that is not a real fact kind (e.g. a
  // connector input key) must not put the form in an unsubmittable state.
  const [kind, setKind] = useState(
    KIND_LABELS[initialKind] ? initialKind : 'esp_boundary');
  const [siteIds, setSiteIds] = useState([]);
  const [designators, setDesignators] = useState('');
  const [basis, setBasis] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [segments, setSegments] = useState('');
  const [gaps, setGaps] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const isCoverage = kind === 'sensor_coverage_attestation';

  const submit = async () => {
    setBusy(true);
    setError(null);
    const value = {};
    if (isCoverage) {
      value.segments_covered = segments.split(',').map((s) => s.trim()).filter(Boolean);
      const g = gaps.split(',').map((s) => s.trim()).filter(Boolean);
      if (g.length) value.known_gaps = g;
    }
    try {
      const extra = designators.split(',').map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post('/skills/compliance/scope-facts', {
        kind,
        scope_ids: [...siteIds, ...extra],
        basis,
        valid_until: validUntil || null,
        value,
      });
      onDone(data);
    } catch (err) {
      setError(errorDetail(err, 'The fact was not recorded.'));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="fact-form">
      <div className="bg-gray-800 border border-gray-600 rounded-xl p-6 w-full max-w-xl">
        <h3 className="text-white font-semibold mb-1">
          Record a scope fact
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Facts are append-only: this records a new fact.
          You are the named asserter.
        </p>
        <label className="block text-xs text-gray-400 mb-1">Kind</label>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200 mb-3">
          {Object.entries(KIND_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <label className="block text-xs text-gray-400 mb-1">Sites this fact speaks about</label>
        <div className="mb-2 space-y-1 border border-gray-700 rounded-lg p-2 max-h-32 overflow-y-auto">
          {(sites || []).map((s) => (
            <label key={s.id} className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={siteIds.includes(s.id)}
                     onChange={() => setSiteIds(siteIds.includes(s.id)
                       ? siteIds.filter((x) => x !== s.id) : [...siteIds, s.id])} />
              {s.site_name}
            </label>
          ))}
        </div>
        <label className="block text-xs text-gray-400 mb-1">Additional scope designators (optional, comma-separated — e.g. a disclosed-gap segment an attestation speaks about)</label>
        <input data-testid="fact-designators" value={designators} onChange={(e) => setDesignators(e.target.value)}
               className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200 mb-3" />
        <label className="block text-xs text-gray-400 mb-1">Basis (required)</label>
        <p className="text-[11px] text-gray-500 mb-1">
          What was reviewed, when, and against what — e.g. “capture configuration reviewed
          2026-08-20 against ESP declaration scenic-esp-01”. You are recorded as the
          asserter separately; do not enter a name here.
        </p>
        <textarea data-testid="fact-basis" value={basis} onChange={(e) => setBasis(e.target.value)}
                  className="w-full h-20 bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200 mb-3" />
        {isCoverage && (
          <>
            <label className="block text-xs text-gray-400 mb-1">Segments covered (comma-separated)</label>
            <input value={segments} onChange={(e) => setSegments(e.target.value)}
                   className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200 mb-3" />
            <label className="block text-xs text-gray-400 mb-1">Known gaps — disclosed as not monitored (comma-separated, optional)</label>
            <input value={gaps} onChange={(e) => setGaps(e.target.value)}
                   className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200 mb-3" />
          </>
        )}
        <label className="block text-xs text-gray-400 mb-1">Valid until (ISO date, optional — absent means due for re-attestation on the 90-day cadence)</label>
        <input value={validUntil} onChange={(e) => setValidUntil(e.target.value)} placeholder="2027-02-15T00:00:00"
               className="w-full bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200 mb-3" />
        {error && <div className="text-red-300 text-xs mb-2">{error}</div>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300">Cancel</button>
          <button data-testid="fact-submit" onClick={submit}
                  disabled={basis.trim().split(/\s+/).filter(Boolean).length < 5 || siteIds.length === 0 || busy}
                  className="px-3 py-1.5 text-sm rounded-lg bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
            Record fact
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ComplianceScopeFacts() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // {kind?}
  const [sites, setSites] = useState([]);
  const [notice, setNotice] = useState(null);
  const [params] = useSearchParams();
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/skills/compliance/scope-facts');
      if (!mounted.current) return;
      setDoc(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Scope facts could not be loaded.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/ce/sites').then(({ data }) => {
      const list = Array.isArray(data) ? data : (Array.isArray(data?.sites) ? data.sites : []);
      setSites(list);
    })
      .catch(() => {});
  }, []);
  useEffect(() => {
    const kind = params.get('kind');
    if (kind) setForm({ kind });
  }, [params]);

  const byKind = {};
  (doc?.facts || []).forEach((f) => { (byKind[f.kind] = byKind[f.kind] || []).push(f); });

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-1">
        <h1 className="text-2xl font-bold text-white">Scope facts</h1>
        <button onClick={() => setForm({})}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800">
          Record a fact
        </button>
      </div>
      <p className="text-sm text-gray-400 mb-5">
        What has been declared about this environment, by whom, and whether it is still current.
        A disclosed known gap is a documented boundary — distinct from nothing having been asserted.
      </p>

      {loading && <div className="text-gray-400 text-sm">Loading facts…</div>}
      {error && (
        <div data-testid="facts-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200">
          {error}
          <button onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}
      {notice && <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3 text-sm text-blue-200 mb-4">{notice}</div>}

      {!loading && !error && doc && (
        Object.keys(KIND_LABELS).filter((k) => byKind[k]).map((k) => (
          <div key={k} className="mb-6">
            <h2 className="text-sm font-semibold text-gray-300 mb-2">{KIND_LABELS[k]}</h2>
            <div className="space-y-2">
              {byKind[k].map((f) => (
                <FactCard key={f.fact_id} f={f} />
              ))}
            </div>
          </div>
        ))
      )}
      {!loading && !error && doc && doc.facts.length === 0 && (
        <div data-testid="not-attested" className="bg-gray-800 rounded-xl border border-gray-700 p-5 text-sm text-gray-300">
          Nothing has been asserted yet — unknown, not disclosed. Record the first fact above.
        </div>
      )}

      {form && (
        <FactForm initialKind={form.kind} sites={sites}
                  onClose={() => setForm(null)}
                  onDone={(data) => {
                    setForm(null);
                    setNotice(data.note || 'Recorded.');
                    load();
                  }} />
      )}
    </div>
    </div>
  );
}
